import { removeFromRenderTree } from './render-tree';
import { EDGE_STYLE_ROUTING, LAYOUT_DIRECTION, } from "../common/user"
import { canConnect, cloneLinqsetNode, EDGE_TYPE, isEditableOrganisationNode, isRoleSystem, isRoleSystemNote, isSupplyChain, LAYOUTS, Linqset, LinqsetNode, NODE_SUPERTYPE, NODE_TYPE } from "../common/linqset-model"
import { Injectable, NgZone } from "@angular/core"
import { UserService } from "../user/user.service"
import { EdgeService } from "../model-services/edge.service"
import { v4 } from "uuid"
import { NodeService } from "../model-services/node.service"
import { LabelService } from "../model-services/label.service"
import { LABEL_TYPES, MODEL_COLOURS } from "../model-services/model-item-enums"
import { AdjacencyTypes, Animator, CircularLayout, CircularLayoutData, Color, ComponentArrangementPolicy, CreateEdgeInputMode, Cursor, EdgeRouter, EdgeRouterData, EdgeStyleIndicatorRenderer, EventRecognizers, ExteriorNodeLabelModel, FolderNodeConverter, FoldingManager, FreeNodePortLocationModel, GraphClipboard, GraphComponent, GraphEditorInputMode, GraphItemTypes, GridInfo, GridRenderer, GridStyle, GroupAlignmentPolicy, GroupLayeringPolicy, GroupNodeLabelModel, GroupNodeStyle, HeatMapRenderer, HierarchicalLayout, HierarchicalLayoutData, IEdge, IEnumerable, IGraph, IHitTester, IInputModeContext, ILayoutAlgorithm, IModelItem, INode, InputModeEventArgs, Insets, IPortCandidate, IPortCandidateProvider, IRenderTreeElement, ITable, LabelStyle, LayoutData, List, LongestPath, MergingFoldingEdgeConverter, ModifierKeys, NodeStyleIndicatorRenderer, OrganicLayout, OrganicLayoutChainSubstructureStyle, OrganicLayoutCycleSubstructureStyle, OrganicLayoutData, OrganicLayoutStarSubstructureStyle, ParentNodeDetectionModes, Point, PolylineEdgeStyle, PortAdjustmentPolicy, PortCandidate, PortCandidateProviderBase, Rect, ReparentStripeHandler, RoutingStyleDescriptor, ShapeNodeShape, ShapeNodeStyle, Size, Stroke, Table, TableEditorInputMode, TimeSpan, TreeLayout, TreeLayoutData, ViewportAnimation } from "@yfiles/yfiles"
import { MatDialog } from "@angular/material/dialog"
import { SelectNodeDialogComponent } from "../model-components/nodes/select-node-dialog.component"
import { NodeStyleSettings } from "../ui-components/svgs/node-svgs"
import { LinqNodeStyle } from "./styles/linq-node-style"
import { gradients } from "../temporal/heat-gradients"
import { hasTag, LayoutTag, nodesBetweenTagged, TAGGED_EDGE_COST } from "./tag-path"
import { modelMainPaths } from "./model-main-path"

// the colours of dimension heatmaps, from cold to hot; legends read them too
export const HEATMAP_GRADIENT = gradients[3].value

const MINIMUM_LAYER_DISTANCE = 40
const NODE_TO_EDGE_DISTANCE = 20
const NODE_TO_NODE_DISTANCE = 40
const EDGE_TO_EDGE_DISTANCE = 20
const MINIMUM_LAST_SEGMENT = 20

@Injectable({
    providedIn: 'root'
})
export class GraphService {
    // each canvas's heatmap. The service is shared by every canvas, and a single heatmap held here could be
    // removed from the wrong canvas, leaving the real one showing
    private heatmaps = new WeakMap<GraphComponent, IRenderTreeElement>()

    // linqsets whose layout the user picked from the toolbar, so a view's default layout doesn't replace it
    readonly chosenLayouts = new WeakSet<Linqset>()

    // layouts animating now; something that zooms to an item waits for them, as a full layout refits the view
    layoutsRunning = 0

    // one port candidate provider per canvas, pointed at whichever model it is showing. A canvas is reused as the user
    // moves between models, and adding a provider for each one kept every model it had shown in memory
    private portProviders = new WeakMap<GraphComponent, {linqset:Linqset}>()

    constructor(private userService:UserService, private edgeService:EdgeService, private nodeService:NodeService, private labelService:LabelService,
        private _zone: NgZone, private dialog: MatDialog) {
    }

    public selectModelItems(graphComponent:GraphComponent, items:IModelItem[]) {
        graphComponent.selection.clear()

        let lastItem:IModelItem = null
        items.forEach(i => {
            if (i) {
                graphComponent.selection.add(i)
                lastItem = i
            }
        })
        if (lastItem) {
            this.zoomToModelItem(graphComponent, lastItem)

        }else graphComponent.fitGraphBounds()
    }

    setupEdgeLabels(graph:IGraph) {
        /*graph.mapperRegistry.createDelegateMapper(
            ILabel.$class,
            PreferredPlacementDescriptor.$class,
            LayoutGraphAdapter.EDGE_LABEL_LAYOUT_PREFERRED_PLACEMENT_DESCRIPTOR_DP_KEY,
            key => {
                const preferredPlacementDescriptor = new PreferredPlacementDescriptor()
                preferredPlacementDescriptor.sideOfEdge = LabelPlacements.ON_EDGE
                preferredPlacementDescriptor.placeAlongEdge = LabelPlacements.AT_CENTER
                preferredPlacementDescriptor.sideReference = LabelSideReferences.RELATIVE_TO_EDGE_FLOW
                preferredPlacementDescriptor.distanceToEdge = 5

                preferredPlacementDescriptor.freeze()
                return preferredPlacementDescriptor
            }
        )

        graph.edgeDefaults.labels.style = new LabelStyle({
            backgroundStroke: Stroke.SKY_BLUE,
            backgroundFill: Fill.ALICE_BLUE
        })*/
    }

    edgeCreator(graphComponent: GraphComponent, linqset:Linqset, context:IInputModeContext, sourceCandidate:IPortCandidate, targetCandidate:IPortCandidate, dummyEdge:IEdge) {
        // the targetCandidate might be null if the edge creation ended prematurely
        if (targetCandidate === null || dummyEdge === null) {
            // no target - no edge
            return null
        }

        let sourceNode = sourceCandidate.owner as INode
        const targetNode = targetCandidate.owner as INode
        const sourceLinqsetNode = sourceNode.tag.node

        if (sourceLinqsetNode.superType === NODE_SUPERTYPE.ROLE || sourceLinqsetNode.superType === NODE_SUPERTYPE.SYSTEM) {
            // if they are holding down ctrl key we will not create a copy of the node,
            if (!(context.canvasComponent.lastPointerEvent.modifiers === ModifierKeys.CONTROL) && graphComponent.graph.edgesAt(sourceNode, AdjacencyTypes.OUTGOING).size !== 0) {
                // create a copy of the node
                const location = new Point(targetNode.layout.bottomRight.x + 100, targetNode.layout.bottomRight.y+100)
                sourceNode = this.nodeService.createNodeAt(linqset, graphComponent.graph, sourceLinqsetNode, v4(), location)
                this.labelService.addLabelToNode(graphComponent.graph, sourceNode, sourceLinqsetNode.name, LABEL_TYPES.NODE_NAME)
            }
        }
        return this.createNewEdge(graphComponent, linqset, sourceNode, targetNode)
    }

    createGridLayout(graphComponent:GraphComponent) {
        const gridInfo = new GridInfo()
        gridInfo.horizontalSpacing = 50
        gridInfo.verticalSpacing = 50

        // Creates grid visualization and adds it to graphComponent
        const grid = new GridRenderer({ gridStyle: GridStyle.LINES, stroke: new Stroke(Color.LIGHT_GRAY, 0.5) })
        graphComponent.renderTree.createElement(graphComponent.renderTree.backgroundGroup, gridInfo, grid)
    }

    public configureTableEditing(graphComponent: GraphComponent): void {
        const graphEditorInputMode = graphComponent.inputMode as GraphEditorInputMode

        // use the undo support from the graph also for all future table instances
        Table.installStaticUndoSupport(graphComponent.graph)

        const reparentStripeHandler = new ReparentStripeHandler()
        reparentStripeHandler.maxColumnLevel = 1
        reparentStripeHandler.maxRowLevel = 1

        // Create a new TEIM instance which also allows drag and drop
        const tableInputMode = new TableEditorInputMode({
            reparentStripeHandler,
            // we set the priority higher than for the handle input mode so that handles win if both gestures are possible
            priority: graphEditorInputMode.handleInputMode.priority + 1
        })
        tableInputMode.stripeDropInputMode.enabled = true

        // Add to GEIM
        graphEditorInputMode.add(tableInputMode)

        // prevent re-parenting of tables into tables by copy & paste
        const clipboard = new GraphClipboard()
        clipboard.parentNodeDetection = ParentNodeDetectionModes.PREVIOUS_PARENT
        graphComponent.clipboard = clipboard

        // prevent selection of the table node
        graphEditorInputMode.selectablePredicate = (item: IModelItem): boolean => {
            return !(item instanceof INode && ITable.getTable(item))
        }
    }

    public initializeStyling(graphComponent:GraphComponent) {
        const {graph} = graphComponent
        const nodeDecorator = graph.decorator.nodes
        const edgeDecorator = graph.decorator.edges;
        const inputMode = <GraphEditorInputMode>graphComponent.inputMode

        //inputMode.showHandleItems = GraphItemTypes.ALL & ~GraphItemTypes.NODE
        inputMode.showHandleItems = GraphItemTypes.NONE
        inputMode.moveViewportInputMode.enabled = true
        this.switchToSelectMode(graphComponent)

        // Style node selection
        // ====================
        const selectionColor = MODEL_COLOURS.NODE_SELECT_COLOR

        //nodeDecorator.focusRenderer.hide()
        //nodeDecorator.highlightRenderer.hide()
        nodeDecorator.selectionRenderer.addFactory((node) =>
            new NodeStyleIndicatorRenderer({
                nodeStyle: new ShapeNodeStyle({fill:Color.TRANSPARENT, stroke:new Stroke(selectionColor, 3.0), shape:ShapeNodeShape.RECTANGLE}),
                zoomPolicy: 'world-coordinates',
                margins: 5
            })
        )

        // Style edge selection
        // ====================
        edgeDecorator.selectionRenderer.addFactory((edge) =>
            new EdgeStyleIndicatorRenderer({
                edgeStyle: new PolylineEdgeStyle({stroke:new Stroke(selectionColor, 4.0)}),
                zoomPolicy: 'world-coordinates'
            })
        )

        // Set focus style
        // ===============
        //graphComponent.focusIndicatorManager.enabled = false;

  		// Create default label style and label model
        graphComponent.graph.nodeDefaults.style = new LinqNodeStyle(new NodeStyleSettings())
        const nodeDefaults = graphComponent.graph.nodeDefaults

        // set a new default size
        nodeDefaults.size = new Size(35, 35)

        nodeDefaults.labels.layoutParameter = ExteriorNodeLabelModel.BOTTOM
        nodeDefaults.ports.autoCleanUp = true

        graph.edgeDefaults.style = this.edgeService.getStyleForEdge(EDGE_TYPE.CREATE)

        this.setupEdgeLabels(graph)
    }

    public createPortHandler(graphComponent:GraphComponent, linqset:Linqset) {
        const existing = this.portProviders.get(graphComponent)
        if (existing) {
            existing.linqset = linqset
            return
        }

        const nodeDecorator = graphComponent.graph.decorator.nodes
        const _this = this

        class LINQNodePortCandidateProvider extends PortCandidateProviderBase {
            constructor(public linqset:Linqset) {
                super();
            }

            node: INode = null!;
            edge: IEdge = null!;

            override getTargetPortCandidates(context: IInputModeContext, source: IPortCandidate) {
                const graph = context.graph;
                if (!graph) {
                    const emptyList:IEnumerable<IPortCandidate> = IEnumerable.prototype;
                    return emptyList
                }

                return this.getTargetPortForSource(graph, context, source, this.node || this.edge.sourceNode)
            }

            /**
             * Determines if we can create an edge from this node
             */
            override getPortCandidates(context:IInputModeContext):IEnumerable<IPortCandidate> {
                const node = this.node || this.edge.sourceNode

                const nodeTag = node.tag && node.tag.node && node.tag.node as LinqsetNode;

                if (!nodeTag || !nodeTag.type) {
                    return new List();
                }
                if (!this.linqset || (this.linqset.isOrganisationModel() && !isEditableOrganisationNode(nodeTag.superType))) {
                    return new List();
                }

                const candidates = new List<IPortCandidate>();
                candidates.add(new PortCandidate(this.node, FreeNodePortLocationModel.CENTER));
                return candidates;
            }

            private getTargetPortForSource(graph:IGraph, context:IInputModeContext, source:IPortCandidate, node:INode):IEnumerable<IPortCandidate> {
                const emptyList:IEnumerable<IPortCandidate> = new List<IPortCandidate>();

                if (_this.canConnectNodes(this.linqset, graph, source.owner as INode, node))
                {
                    return IPortCandidateProvider.fromNodeCenter?IPortCandidateProvider.fromNodeCenter(node).getTargetPortCandidates(context, source):emptyList;
                }
                return emptyList;
            }

        }

        const singletonPortCandidateProvider = new LINQNodePortCandidateProvider(linqset);
        this.portProviders.set(graphComponent, singletonPortCandidateProvider)

        nodeDecorator.portCandidateProvider.addFactory((node:INode) => {
            singletonPortCandidateProvider.node = node;
            return singletonPortCandidateProvider;
        });
    }

    public initializeGrouping(graphComponent:GraphComponent) {
        const foldingManager = new FoldingManager();
        const foldingView = foldingManager.createFoldingView();
        //foldingManager.foldingEdgeConverter = new LabeledMergingFoldingEdgeConverter()
        foldingManager.foldingEdgeConverter = new MergingFoldingEdgeConverter()
        foldingManager.masterGraph.undoEngineEnabled = true

        foldingManager.folderNodeConverter = new FolderNodeConverter({
            folderNodeDefaults: { copyLabels: true, size: new Size(140, 80) }
        })

        graphComponent.graph = foldingView.graph
        const {graph} = graphComponent

        graph.groupNodeDefaults.style = new GroupNodeStyle({ tabFill: '#0b7189', groupIcon:'minus', drawShadow: true, tabHeight:40, minimumContentAreaSize: new Size(140, 40) })

        // Set a label style with right-aligned text
        graph.groupNodeDefaults.labels.style = new LabelStyle({
            horizontalTextAlignment: 'left',
            textFill: 'white',
            wrapping: 'wrap-character-ellipsis',
            minimumSize: new Size(140, 20)
        })

        // Place the label inside the tab area of the group node
        // GroupNodeLabelModel is usually the most appropriate label model for GroupNodeStyle
        graph.groupNodeDefaults.labels.layoutParameter = new GroupNodeLabelModel().createTabParameter()

        return foldingView
    }

    private createHierarchicalLayout(orientation:LAYOUT_DIRECTION, edgeRouting:EDGE_STYLE_ROUTING, incremental:boolean) {
        const layout = new HierarchicalLayout({
            layoutOrientation: orientation,
            minimumLayerDistance: MINIMUM_LAYER_DISTANCE,
            stopDuration: '5s',
            nodeDistance: NODE_TO_NODE_DISTANCE,
            nodeToEdgeDistance: NODE_TO_EDGE_DISTANCE,
            edgeDistance: EDGE_TO_EDGE_DISTANCE,
            automaticEdgeGrouping: true,
            fromSketchMode: incremental
        })
        layout.coordinateAssigner.symmetryOptimizationStrategy = 'weak'
        layout.defaultEdgeDescriptor.routingStyleDescriptor = new RoutingStyleDescriptor(edgeRouting)
        layout.defaultEdgeDescriptor.backLoopRouting = true

        layout.groupLayeringPolicy = GroupLayeringPolicy.RECURSIVE
        layout.groupAlignmentPolicy = GroupAlignmentPolicy.CENTER

        layout.componentArrangementPolicy = ComponentArrangementPolicy.COMPACT

        return layout
    }

    async runLayoutForLinqset(graphComponent: GraphComponent, linqset:Linqset, incremental:boolean=false) {
        this.runLayout(graphComponent, linqset.layout, incremental, linqset.layoutTag, linqset)
    }

    // the linqset gives the insights the main path layout follows the items by
    async runLayout(graphComponent: GraphComponent, layout:LAYOUTS, incremental:boolean=false, layoutTag?:LayoutTag, linqset?:Linqset) {
        const inputMode = <GraphEditorInputMode>graphComponent.inputMode
        if (inputMode) inputMode.enabled = false

        const preferences = this.userService.getUserPreferences()
        const edgeRouting = preferences?preferences.edgeRoutingStyle:EDGE_STYLE_ROUTING.POLYLINE
        const orientation = graphComponent.graph.tag?.orientation||(preferences?preferences.graphDirection:LAYOUT_DIRECTION.DOWN)

        let layoutAlgorithm:ILayoutAlgorithm
        let layoutData:LayoutData

        // the longest path layout highlights its edges; any other layout, or running it again, starts from none
        graphComponent.graph.edges.forEach(e => { if (e.tag?.highlight) e.tag.highlight = false })

        if (layout === LAYOUTS.CIRCULAR) {
            layoutAlgorithm = new CircularLayout({
                partitioningPolicy: 'bcc-compact',
            })
            layoutData = new CircularLayoutData({
                nodeTypes: (node:INode) => node.tag?.node?.superType,

            })
        }else if (layout === LAYOUTS.ORGANIC) {
            const olayout = new OrganicLayout({
                cycleSubstructureStyle: OrganicLayoutCycleSubstructureStyle.CIRCULAR,
                starSubstructureStyle: OrganicLayoutStarSubstructureStyle.RADIAL,
                chainSubstructureStyle: OrganicLayoutChainSubstructureStyle.RECTANGULAR
            })

            layoutData = new OrganicLayoutData({
                nodeTypes: (node:INode) => node.tag?.node?.superType
            })

            olayout.deterministic = true
            //olayout.defaultMinimumNodeDistance = 20
            //olayout.defaultPreferredEdgeLength = 20
            olayout.compactnessFactor = 0.8

            layoutAlgorithm = olayout

        }else {

            layoutAlgorithm = this.createHierarchicalLayout(orientation, edgeRouting, incremental)

            const hdata = new HierarchicalLayoutData({
                nodeTypes: (node:INode) => node.tag?.node?.superType||node.tag?.group,
                incrementalNodes: (node) => node.tag?.includeInLayout?true:false,
                ports: {
                    sourcePortGroupIds: (edge: IEdge): any => edge.targetNode,
                    targetPortGroupIds: (edge: IEdge): any => edge.sourceNode
                }
            })
            if (layout === LAYOUTS.HIERARCHIC_LONGEST) {
                const longestPathEdges = new LongestPath().run(graphComponent.graph).edges
                longestPathEdges.filter(e => e.tag).forEach(e => e.tag.highlight = true)
                hdata.criticalEdgePriorities = (edge) => (longestPathEdges.contains(edge) ? 10 : 1)
            }else if (layout === LAYOUTS.HIERARCHIC_MAIN) {
                // each part of the model lined up along its main path: where most items go
                const pathEdges = new Set(modelMainPaths(graphComponent.graph, linqset).flatMap(p => p.via))
                pathEdges.forEach(e => { if (e.tag) e.tag.highlight = true })
                hdata.criticalEdgePriorities = (edge) => (pathEdges.has(edge) ? 10 : 1)
            }else if (layout === LAYOUTS.HIERARCHIC_TAG && layoutTag) {
                // the longest path through the nodes with the tag and what joins them, counting tagged nodes most
                const graph = graphComponent.graph
                const tagged = (node:INode) => hasTag(node.tag?.node?.properties, layoutTag)
                const between = nodesBetweenTagged(graph.nodes.toArray(),
                    node => graph.outEdgesAt(node).map(e => e.targetNode).toArray(),
                    node => graph.inEdgesAt(node).map(e => e.sourceNode).toArray(),
                    tagged)
                if (between.size > 1) {
                    const tagPathEdges = new LongestPath({
                        subgraphNodes: (node:INode) => between.has(node),
                        costs: (edge:IEdge) => tagged(edge.targetNode) ? TAGGED_EDGE_COST : 1,
                    }).run(graph).edges
                    tagPathEdges.filter(e => e.tag).forEach(e => e.tag.highlight = true)
                    hdata.criticalEdgePriorities = (edge) => (tagPathEdges.contains(edge) ? 10 : 1)
                }
            }

           layoutData = hdata
        }
        this.layoutsRunning++
        try {
            await this._zone.runOutsideAngular(async () => {
                await graphComponent.applyLayoutAnimated({
                    layout:layoutAlgorithm,
                    layoutData,
                    animationDuration: '1s',
                    animateViewport: !incremental,
                    portAdjustmentPolicies: PortAdjustmentPolicy.ALWAYS
                })
                graphComponent.graph.nodes.filter(n => n.tag?.includeInLayout).forEach(n => n.tag.includeInLayout = false)
                // the viewport was animated for the canvas size when the layout started; side panels opening
                // meanwhile change it, so fit again for the size now
                if (!incremental) graphComponent.fitGraphBounds()
            })
        } catch(error) {
            console.log("*****ERRROR", error)
        }finally {
            this.layoutsRunning--
            if (inputMode) inputMode.enabled = true
        }
    }

    /**
     * Re-Route the edges.
     *
     * The EdgeRouter will be used with its routingPolicy set to either PATH_AS_NEEDED or SEGMENTS_AS_NEEDED.
     * That way, the EdgeRouter itself will determine which edges need to be re-routed.
     * The different policies are selected with  the RoutingPolicy ComboBox in the tool bar:
     *    RoutingPolicy.PATH_AS_NEEDED: re-routes the entire path of a "dirty" edge.
     *    RoutingPolicy.SEGMENTS_AS_NEEDED: re-routes a dirty edge in a way that the
     *                                      existing path is kept as much as possible.
     */
    async reRouteEdges(graphComponent:GraphComponent): Promise<void> {
        const layoutData = new EdgeRouterData()

        // route only the selected edges
        layoutData.scope.edges = graphComponent.selection.edges

        const edgeRouter = new EdgeRouter()

        graphComponent.graph.applyLayout(edgeRouter, layoutData)
    }

    async zoomToSetOfModelNodes(graphComponent:GraphComponent, nodes:INode[]) {
        if (nodes.length === 0) return
        try {
            // the bounds of the nodes, including their full size
            const dimensions = nodes.reduce((d, n) => {
                const layout = n.layout
                d.x1 = Math.min(d.x1, layout.x)
                d.x2 = Math.max(d.x2, layout.maxX)
                d.y1 = Math.min(d.y1, layout.y)
                d.y2 = Math.max(d.y2, layout.maxY)
                return d
            }, {x1:Infinity, x2:-Infinity, y1:Infinity, y2:-Infinity})

            let rect = new Rect(dimensions.x1, dimensions.y1, dimensions.x2-dimensions.x1, dimensions.y2-dimensions.y1)
            rect = rect.getEnlarged(new Insets(100))
            const viewportAnimation = new ViewportAnimation(graphComponent, rect, new TimeSpan(1000))
            const animator = new Animator(graphComponent)
            animator.allowUserInteraction = true
            await animator.animate(viewportAnimation.createEasedAnimation(0, 1))
        }catch(err) {
            console.error(err)
        }
    }

    async zoomToModelItem(graphComponent:GraphComponent, item?:IModelItem) {
        try {
            if (!item) item = graphComponent.currentItem
            if (!item) return

            let rect:Rect

            if (item instanceof INode) {
                rect = item.layout.toRect()
            }else if (item instanceof IEdge) {
                rect = new Rect(
                    item.sourcePort.location.toPoint(),
                    item.targetPort.location.toPoint()
                )
            }

            rect = rect.getEnlarged(new Insets(350))
            const viewportAnimation = new ViewportAnimation(graphComponent, rect, new TimeSpan(1000))
            const animator = new Animator(graphComponent)
            animator.allowUserInteraction = true
            await animator.animate(viewportAnimation.createEasedAnimation(0, 1))
        }catch(err) {
            // best efforts, just ignore if doesnt work
        }
    }

    private moveNode(node:INode, graph:IGraph, existingModelKey:string, newModelKey:string, nodes:INode[], edges:IEdge[]) {
        if (!nodes.find(n => n === node)) {
            if (isSupplyChain(node.tag.node.superType) || node.tag.node.superType === NODE_SUPERTYPE.NOTES) {
                node.tag.node.modelKey = newModelKey
                nodes.push(node)
            }

            const nextEdges = graph.edgesAt(node).filter(e => (e.sourceNode===node||isRoleSystemNote(e.sourceNode.tag.node.superType)))

            nextEdges.filter(e => e.tag && e.tag.edge && e.tag.edge.modelKey === existingModelKey && !edges.find(ee => ee===e)).forEach(e => {
                e.tag.edge.modelKey = newModelKey
                edges.push(e)
                const nextNode = e.sourceNode===node?e.targetNode:e.sourceNode
                this.moveNode(nextNode, graph, existingModelKey, newModelKey, nodes, edges)
            })
        }
    }

    createNewEdge(graphComponent:GraphComponent, linqset:Linqset, sourceNode:INode, targetNode:INode) {
        if (!this.canConnectNodes(linqset, graphComponent.graph, sourceNode, targetNode)) return null
        if (targetNode.tag.node.superType === NODE_SUPERTYPE.ACTION && (sourceNode.tag.node.superType === NODE_SUPERTYPE.ROLE || sourceNode.tag.node.superType === NODE_SUPERTYPE.SYSTEM)) {
            // connecting people or a system to an action node
            const edgesToAction = graphComponent.graph.edgesAt(targetNode, AdjacencyTypes.INCOMING)
            if (sourceNode.tag.node.superType === NODE_SUPERTYPE.ROLE)
            {
                // connecting a role to the action
                const systemEdges = edgesToAction.filter(e => e.sourceNode.tag.node.superType === NODE_SUPERTYPE.SYSTEM).toArray()
                systemEdges.forEach(systemEdge => {
                    // action node has a system connected so insert new people node in between
                    systemEdge.tag.edge.type = EDGE_TYPE.USEBY
                    graphComponent.graph.setEdgePorts(systemEdge, systemEdge.sourcePort, graphComponent.graph.addPort(sourceNode))
                })
            }else if (sourceNode.tag.node.superType === NODE_SUPERTYPE.SYSTEM)
            {
                // we do this so that it dosnt throuw a collection modified exception
                const edgesToRemove = edgesToAction.filter(e => e.sourceNode.tag.node.superType === NODE_SUPERTYPE.ROLE).map(e => e).toArray()
                edgesToRemove.forEach(re => {
                    // we replace the connected role so remove it which also removes the edge
                    graphComponent.graph.remove(re.sourceNode)
                })
            }
        }
        return this.edgeService.createNewEdge(graphComponent.graph, linqset, sourceNode, targetNode)
    }

    edgeCreationListener(graphComponent:GraphComponent, createEdgeInputMode:CreateEdgeInputMode) {
        const sourcePort = createEdgeInputMode.startPortCandidate
        const linqsetNode = sourcePort?.owner?.tag?.node
        if (!linqsetNode) return

        // Select the node we are dragging from
        this.clearSelection(graphComponent);
        this.setSelected(graphComponent, sourcePort.owner, true);

        if (canConnect(linqsetNode.type, NODE_TYPE.INFORMATION) || canConnect(linqsetNode.type, NODE_TYPE.ACTION)) {
            createEdgeInputMode.dragCursor = Cursor.COPY
            createEdgeInputMode.validBendCursor = Cursor.COPY;
        }

        const edgeType = this.edgeService.getEdgeType(linqsetNode)
        createEdgeInputMode.previewEdge.tag = {id:v4(), type:edgeType, properties: {type:edgeType}, source:sourcePort.owner}

        const edgeStyle = this.edgeService.getStyleForEdge(edgeType)
        if (edgeStyle) createEdgeInputMode.previewGraph.setStyle(createEdgeInputMode.previewEdge, edgeStyle)
    }

    edgeCanceledListener(graphComponent:GraphComponent, linqset:Linqset, mouseCurrentLocation:Point, sender:CreateEdgeInputMode, eventArgs:InputModeEventArgs) {
        const sourceNode = sender.previewEdge.sourceNode

        if (sourceNode instanceof INode && sourceNode !== null && sourceNode.tag.node !== null) {

            const hitEnumerator = eventArgs.context.lookup(IHitTester)
            const hits = hitEnumerator?.enumerateHits(eventArgs.context, mouseCurrentLocation).getEnumerator();
            const hit = hits?.moveNext() ? hits.current : null;
            if(hit === null) {
                // See if we can add an info node, or action node
                let prototypeToAdd = null;
                if (canConnect(sourceNode.tag.node.type, NODE_TYPE.INFORMATION)) {
                    prototypeToAdd = NODE_TYPE.INFORMATION;
                } else if (canConnect(sourceNode.tag.node.type, NODE_TYPE.ACTION)) {
                    prototypeToAdd = NODE_TYPE.ACTION;
                }else return

                const newLinqsetNode = this.nodeService.createLinqsetNode(prototypeToAdd, linqset)
                const newNode = this.nodeService.createNodeAt(linqset, graphComponent.graph, newLinqsetNode, v4(), mouseCurrentLocation)
                this.edgeService.createNewEdge(graphComponent.graph, linqset, sourceNode, newNode)
                graphComponent.currentItem = newNode
            }
        }
    }

    canConnectLinqsetNodes(linqset:Linqset, sourceNode:LinqsetNode, targetNode:LinqsetNode, ignoreSameEdge:boolean = false) {
        if (!targetNode || !sourceNode) return false

        if (sourceNode.type === NODE_TYPE.EXTERNAL_ORGANISATION) {
            // cannot be another role connected to this action
            if (linqset.edges.find(e => e.sourceKey !== sourceNode._key && e.targetKey === targetNode._key && e.type === EDGE_TYPE.PERFORM)) return false
        }

        // can connect an external info node from another model to a capture node
        if (targetNode.type === NODE_TYPE.CAPTURE && sourceNode.superType === NODE_SUPERTYPE.INFORMATION && targetNode.modelKey !== sourceNode.modelKey && targetNode.external) return true
        // cannot connect an action node from this model to an external node from another model
        if (targetNode.external && targetNode.superType === NODE_SUPERTYPE.INFORMATION && sourceNode.superType === NODE_SUPERTYPE.ACTION && targetNode.modelKey !== linqset.modelKey) return false

        // check whether type connection allowed
        if (!canConnect(sourceNode.type, targetNode.type)) return false

        if (!isRoleSystem(sourceNode.superType) && !ignoreSameEdge) {
            if (linqset.edges.find(e => (e.sourceKey === sourceNode._key && e.targetKey === targetNode._key))) return false
        }

        return true
    }

    canConnectNodes(linqset:Linqset, graph:IGraph, from:INode, to:INode) {
        if (!to.tag?.node || !from.tag?.node || from.tag.disabledEdge || to.tag.disabledEdge) return false

        try {
            if (!this.canConnectLinqsetNodes(linqset, from.tag.node, to.tag.node)) return false

            if (from.tag.node.superType === NODE_SUPERTYPE.SYSTEM) {
                // cannot connect system node to same node twice
                if (graph.edges.find(e => e.sourceNode == from && e.targetNode === to)) return false
            }

            // check for loops
            if (isSupplyChain(from.tag.node.superType) && isSupplyChain(to.tag.node.superType)) {
                const isLoop = {value:false}
                this.nodeCausesLoop(graph, to, from, isLoop, [])
                return !isLoop.value
            }
            return true
        }catch(err) {
            // handles when edge not in graph error
            return false
        }
    }

    nodeCausesLoop(graph:IGraph, node:INode, nodeToCheck:INode, isLoop:any, processed:INode[]) {
        // set to true if find node to check in the downstream supply chain
        if (node === nodeToCheck) isLoop.value = true

        // this should stop all traversals once the common isLoop boolean is set to truw
        if (!isLoop.value) {
            if (processed.find(n => n===node)) return   // we have processed this node to exit
            processed.push(node)
            for (const e of graph.edgesAt(node, AdjacencyTypes.OUTGOING).filter(e => e.targetNode.tag.node && isSupplyChain(e.targetNode.tag.node.superType)).toArray()) {
                this.nodeCausesLoop(graph, e.targetNode, nodeToCheck, isLoop, processed)
            }
        }
    }

    moveSelection (graphComponent:GraphComponent, nodesSelected:INode[], existingModelKey:string, newModelKey:string) {
        // only action nodes
        const {graph} = graphComponent

        const nodes:INode[] = []
        const edges:IEdge[] = []

        // first move all nodes and edges up/down stream of the selected nodes
        nodesSelected.forEach(n => {
            this.moveNode(n, graphComponent.graph, existingModelKey, newModelKey, nodes, edges)
        })

        // now check the connections between this model and new one, creates joins where needed
        nodes.filter(n => n.tag.node.superType === NODE_SUPERTYPE.ACTION).forEach(n => {
            // edges may have been moved while processing another node so check whether in edge in this model
            const joinEdges = graph.edgesAt(n, AdjacencyTypes.INCOMING).filter(e => e.tag.edge.modelKey === existingModelKey && e.sourceNode.tag.node.superType === NODE_SUPERTYPE.INFORMATION)
            joinEdges.forEach(e => {
                // move edge to new model and make info node external
                e.tag.edge.type = EDGE_TYPE.JOINS
                e.tag.edge.modelKey = newModelKey
                e.sourceNode.tag.node.external = true
                edges.push(e)
                nodes.push(e.sourceNode)
            })
        })
        return {nodes, edges}
    }

    switchToPanMode(graphComponent:GraphComponent) {
        const inputMode = graphComponent.inputMode as GraphEditorInputMode

        inputMode.defaultCursor = Cursor.GRAB;
        inputMode.moveViewportInputMode.cancel();
        inputMode.moveViewportInputMode.beginRecognizer = EventRecognizers.MOUSE_LEFT_DOWN;
    }

    switchToSelectMode(graphComponent:GraphComponent) {
        const inputMode = graphComponent.inputMode as GraphEditorInputMode

        inputMode.moveViewportInputMode.cancel();
        inputMode.defaultCursor = Cursor.AUTO;
        inputMode.moveViewportInputMode.beginRecognizer = (evt, sender) =>
            EventRecognizers.MOUSE_LEFT_DOWN(evt, sender) &&
            EventRecognizers.ALT_IS_DOWN(evt, sender)
    }

    /**
     * The only way we should clear the graph.
     * Except for undo and redo in a command.
     */
    clearSelection(graphComponent:GraphComponent) {
        graphComponent?.selection.clear();
    }

    /**
     * The only way we should select or deselect an item with code.
     * Except for undo and redo in a command.
     */
    setSelected(graphComponent:GraphComponent, item:IModelItem, selected:any) {
        if(item) {
            if (selected) graphComponent?.selection.add(item);
            else graphComponent?.selection.remove(item);
        }
    }

    // holding ctrl adds to the current selection
    public selectUpstream(graphComponent:GraphComponent, ev:PointerEvent, node:INode) {
        if (!ev?.ctrlKey) graphComponent.selection.clear()

        this.selectUpstreamNode(graphComponent, node)
    }

    private selectUpstreamNode(graphComponent:GraphComponent, node:INode) {
        graphComponent.selection.add(node)

        graphComponent.graph.edgesAt(node, AdjacencyTypes.INCOMING).filter(e => !graphComponent.selection.includes(e)).forEach(e => {
            graphComponent.selection.add(e)
            this.selectUpstreamNode(graphComponent, e.sourceNode)
        })
    }

    public selectDownstream(graphComponent:GraphComponent, ev:PointerEvent, node:INode) {
        if (!ev?.ctrlKey) graphComponent.selection.clear()

        this.selectDownstreamNode(graphComponent, node)
    }

    public selectDownstreamNode(graphComponent:GraphComponent, node:INode) {
        graphComponent.selection.add(node)

        graphComponent.graph.edgesAt(node).filter(e => !graphComponent.selection.includes(e)).forEach(e => {
            if (e.sourceNode === node && e.targetNode.tag.node && isSupplyChain(e.targetNode.tag.node.superType)) {
                // outgoing info or action nodes
                graphComponent.selection.add(e)
                this.selectDownstreamNode(graphComponent, e.targetNode)
            }else if (e.targetNode === node && e.sourceNode.tag.node && isRoleSystemNote(e.sourceNode.tag.node.superType)) {
                // incoming role, system or notes nodes
                graphComponent.selection.add(e)
                graphComponent.selection.add(e.sourceNode)
                // we go back one more step
                graphComponent.graph.edgesAt(e.sourceNode, AdjacencyTypes.INCOMING)
                    .filter(e2 => e2.sourceNode.tag.node && isRoleSystemNote(e2.sourceNode.tag.node.superType))
                    .forEach(e2 => {
                        graphComponent.selection.add(e2)
                        graphComponent.selection.add(e2.sourceNode)
                    })
            }
        })
    }

    public selectConnected(graphComponent:GraphComponent, targetNodes:INode[]) {
        if (targetNodes.length === 1) {
            this.selectUpstreamNode(graphComponent, targetNodes[0])
            this.selectDownstreamNode(graphComponent, targetNodes[0])
        }else {
            const pathsToHighlight = [];

            // Find paths between every unique combination of your selected nodes
            for (let i = 0; i < targetNodes.length; i++) {
                for (let j = i + 1; j < targetNodes.length; j++) {
                    const start = targetNodes[i];
                    const end = targetNodes[j];

                    const allPathsBetweenPair = [];
                    this.findAllPaths(graphComponent.graph, start, end, new Set(), [], allPathsBetweenPair);
                    this.findAllPaths(graphComponent.graph, end, start, new Set(), [], allPathsBetweenPair);

                    pathsToHighlight.push(...allPathsBetweenPair);
                }
            }

            // Clear any previous highlights
            graphComponent.selection.clear()

            // Flatten your array of paths to get all unique edges and nodes involved
            const uniqueEdges: Set<IEdge> = new Set();
            const uniqueNodes: Set<INode> = new Set();

            pathsToHighlight.forEach(path => {
            path.forEach((edge:IEdge) => {
                uniqueEdges.add(edge);
                uniqueNodes.add(edge.sourceNode);
                uniqueNodes.add(edge.targetNode);
            });
            });

            // Add elements to the highlight manager
            uniqueNodes.forEach(node => graphComponent.selection.add(node));
            uniqueEdges.forEach(edge => graphComponent.selection.add(edge));
        }
    }

    private findAllPaths(graph:IGraph, current:INode, end:INode, visited:Set<INode>, currentPath:IEdge[], allPaths:IEdge[][]): void {
        if (current === end) {
            allPaths.push([...currentPath]);
            return;
        }

        visited.add(current);

        // Explore all outgoing edges (use graph.edgesAt for undirected graphs)
        for (const edge of graph.outEdgesAt(current)) {
            const nextNode = edge.targetNode;

            if (!visited.has(nextNode)) {
                currentPath.push(edge);
                this.findAllPaths(graph, nextNode, end, visited, currentPath, allPaths);
                currentPath.pop(); // Backtrack
            }
        }

        visited.delete(current); // Backtrack
   }

    public cloneNode(graphComponent:GraphComponent, linqset:Linqset, node:INode):INode {
        const {graph} = graphComponent
        const edges = graph.edgesAt(node)

        const newNode = cloneLinqsetNode(node.tag.node)

        const clone = this.nodeService.createNodeAt(linqset, graph, newNode, v4(), new Point(node.layout.topRight.x + 50, node.layout.topRight.y))
        edges.forEach(e => {
            if (e.targetNode === node) {
                if (e.sourceNode.tag.node.superType === NODE_SUPERTYPE.ROLE || e.sourceNode.tag.node.superType === NODE_SUPERTYPE.SYSTEM) {
                    const newVisualisation = this.nodeService.createNodeAt(linqset, graph, e.sourceNode.tag.node, v4(), new Point(node.layout.topRight.x + 50, node.layout.topRight.y))
                    this.edgeService.createNewEdge(graph, linqset, newVisualisation, clone)
                    const nextEdges = graph.edgesAt(e.sourceNode, AdjacencyTypes.INCOMING)
                    nextEdges.forEach(e2 => {
                        const newVisualisation2 = this.nodeService.createNodeAt(linqset, graph, e2.sourceNode.tag.node, v4(), new Point(node.layout.topRight.x + 50, node.layout.topRight.y))
                        this.edgeService.createNewEdge(graph, linqset, newVisualisation2, newVisualisation)
                    })
                }else this.edgeService.createNewEdge(graph, linqset, e.sourceNode, clone)
            }
        })
        graphComponent.selection.clear();
        graphComponent.selection.add(clone)
        graphComponent.currentItem = clone
        this.runLayoutForLinqset(graphComponent, linqset)

        return clone
    }

    /**
     * Helper function that updates the node selection state when the context menu is opened on a node.
     * @param {yfiles.view.GraphComponent} graphComponent The given graphComponent
     * @param {yfiles.graph.INode} node The node or <code>null</code>.
     */
    public updateSelection(graphComponent:GraphComponent, node:INode) {
        if (node === null) {
            // clear the whole selection
            graphComponent.selection.clear()
        } else if (!graphComponent.selection.includes(node)) {
            if (graphComponent.lastPointerEvent.modifiers !== ModifierKeys.CONTROL) {
                // no - clear the remaining selection
                graphComponent.selection.clear()
            }
            // and select the node
            graphComponent.selection.add(node)
            // also update the current item
            graphComponent.currentItem = node
        }
    }

    public addNodeToExistingNode(graphComponent:GraphComponent, linqset:Linqset, node:INode, type?:NODE_TYPE):INode {
        const nodeLocation = node.layout.bottomRight

        if (!type) {
            if (node.tag.node.superType == NODE_SUPERTYPE.ACTION) {
                type = NODE_TYPE.INFORMATION
            }else if (node.tag.node.superType == NODE_SUPERTYPE.INFORMATION) {
                type = NODE_TYPE.ACTION
            }
        }
        // other kinds of node have no obvious next node
        if (!type) return null
        const linqsetNode = this.nodeService.createLinqsetNode(type, linqset)
        const newNode = nodeLocation?this.nodeService.createNodeAt(linqset, graphComponent.graph, linqsetNode, v4(), nodeLocation):
                    this.nodeService.createNode(linqset, linqsetNode, v4(), graphComponent.graph)

        this.createNewEdge(graphComponent, linqset, node, newNode)

        graphComponent.selection.clear();
        graphComponent.selection.add(newNode)
        graphComponent.currentItem = newNode
        this.runLayoutForLinqset(graphComponent, linqset)

        return newNode
    }

    public addNodeToNodes(graphComponent:GraphComponent, linqset:Linqset, nodes:INode[], selection:LinqsetNode[], type:NODE_TYPE) {
        this.dialog.open(SelectNodeDialogComponent, {
            data: {nodes:selection, numNodes:nodes.length, type},
            width: "600px",
            hasBackdrop:true
        }).afterClosed().subscribe(async (result:any) => {
            if (result) {
                let node:LinqsetNode
                if (typeof result === 'string') {
                    node = this.nodeService.createLinqsetNode(type, linqset)
                    node.name = result
                }else node = result as LinqsetNode

                nodes.filter(n => this.canConnectLinqsetNodes(linqset, node, n.tag.node)).forEach(n => {
                    const iNode = this.nodeService.createNode(linqset, node, v4(), graphComponent.graph)
                    this.createNewEdge(graphComponent, linqset, iNode, n)
                })

                this.runLayoutForLinqset(graphComponent, linqset)
            }
        })

    }

    public removeHeatmap(graphComponent:GraphComponent) {
        const element = graphComponent && this.heatmaps.get(graphComponent)
        if (!element) return
        this.heatmaps.delete(graphComponent)
        removeFromRenderTree(graphComponent, element)
    }

    public hasHeatmap(graphComponent:GraphComponent) {
        return !!graphComponent && this.heatmaps.has(graphComponent)
    }

    public toggleHeatmap(graphComponent:GraphComponent, linqset:Linqset) {
        if (this.hasHeatmap(graphComponent)) this.removeHeatmap(graphComponent)
        else return this.addHeatmap(graphComponent, linqset.maxEdgeValue, linqset.maxNodeValue, linqset.isOrganisationModel())

        return undefined
    }

    // allNodes makes every node with a value glow, not only models, groups and the supply chain
    public addHeatmap(graphComponent:GraphComponent, maxEdgeValue:number, maxNodeValue:number, isOrgModel:boolean, allNodes:boolean = false) {
        // how hot an item is, from 0 to 1 (the renderer's range, so negative values are cold); items without a value are cold
        const heat = (value:number, max:number) => Math.min(1, Math.max(0, value/max))
        const heatProvider = (item: INode | IEdge): number => {
            if (!maxEdgeValue) return 0
            const value = Number(item.tag?.value) || 0
            if (item.tag?.edge) {
                return heat(value, maxEdgeValue)
            }else {
                if (maxNodeValue && (allNodes || (item.tag?.model && isOrgModel) || item.tag?.group || (item.tag?.node && isSupplyChain(item.tag?.node.superType))))
                    return heat(value, maxNodeValue)
            }
            return 0
        }

        // one heatmap per canvas
        this.removeHeatmap(graphComponent)

        const renderTree = graphComponent.renderTree
        const heatMapRenderer = new HeatMapRenderer({
                nodeHeatProvider: heatProvider,
                edgeHeatProvider: heatProvider,
                gradient: HEATMAP_GRADIENT
            })

        this.heatmaps.set(graphComponent, renderTree.createElement(renderTree.backgroundGroup, graphComponent.graph, heatMapRenderer))
        return heatMapRenderer
    }
}
